/**
 * What this device is allowed to do.
 *
 * The rule that shapes everything here: **the server decides, the client only
 * displays.** Nothing in this file is a security boundary — every answer it
 * gives is a guess that the next API call will confirm or overrule. Someone who
 * edits localStorage in devtools gets the shell of the app with no data in it,
 * which is the correct amount of damage.
 *
 * The second rule: **start-up never waits on the network.** A family at a
 * campsite opens this app with no signal, and a spinner that never resolves is
 * the worst possible failure. So a hint in localStorage decides what to render
 * immediately, and only a definitive answer from the server changes it. A
 * network error changes nothing.
 */

import { create } from 'zustand';

export type AuthStatus =
  /** No hint yet — first run on this device. The only case that waits. */
  | 'unknown'
  /** Server said yes, or a previous run did and we have not heard otherwise. */
  | 'allowed'
  /** Server said no, in so many words. */
  | 'locked'
  /** No hint and no reachable server. Nothing to show, nothing to blame. */
  | 'unreachable';

export type Role = 'player' | 'admin';

export interface AuthState {
  status: AuthStatus;
  role: Role | null;
  profileId: string | null;
  label: string | null;
  /** True once a real answer has come back in this session. */
  confirmed: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  /** Called by the API layer when a request comes back 401. */
  onUnauthorized: () => void;
}

const HINT_KEY = 'bb.auth.hint';

interface Hint {
  role: Role;
  profileId: string | null;
  label: string | null;
}

/*
 * Decoration, not authority. It exists so the app can paint the right screen on
 * the first frame while offline; it grants nothing. Everything it describes is
 * re-checked server-side on every request.
 */
function readHint(): Hint | null {
  try {
    const raw = localStorage.getItem(HINT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Hint>;
    if (parsed.role !== 'player' && parsed.role !== 'admin') return null;
    return {
      role: parsed.role,
      profileId: typeof parsed.profileId === 'string' ? parsed.profileId : null,
      label: typeof parsed.label === 'string' ? parsed.label : null,
    };
  } catch {
    return null;
  }
}

function writeHint(hint: Hint | null): void {
  try {
    if (hint) localStorage.setItem(HINT_KEY, JSON.stringify(hint));
    else localStorage.removeItem(HINT_KEY);
  } catch {
    // Private browsing, quota, a locked-down iOS profile. Losing the hint costs
    // one spinner on the next cold start, so there is nothing to handle.
  }
}

const hint = readHint();

export const useAuth = create<AuthState>()((set, get) => ({
  status: hint ? 'allowed' : 'unknown',
  role: hint?.role ?? null,
  profileId: hint?.profileId ?? null,
  label: hint?.label ?? null,
  confirmed: false,

  refresh: async () => {
    try {
      const response = await fetch('/api/auth/me', { credentials: 'same-origin' });
      if (!response.ok) throw new Error(String(response.status));

      const body = (await response.json()) as {
        authenticated: boolean;
        role?: Role;
        profileId?: string | null;
        label?: string | null;
      };

      if (!body.authenticated || !body.role) {
        writeHint(null);
        set({
          status: 'locked',
          role: null,
          profileId: null,
          label: null,
          confirmed: true,
        });
        return;
      }

      const fresh: Hint = {
        role: body.role,
        profileId: body.profileId ?? null,
        label: body.label ?? null,
      };
      writeHint(fresh);
      set({ status: 'allowed', ...fresh, confirmed: true });
    } catch {
      /*
       * Unreachable. This must NOT lock the app: it is the campsite, the
       * tunnel, the server restarting. Whatever the hint said stands, and the
       * next successful request settles it.
       */
      if (get().status === 'unknown') set({ status: 'unreachable' });
    }
  },

  logout: async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' });
    } catch {
      // The cookie may survive, but the local state must not — otherwise
      // "afmelden" appears to do nothing when the network is out.
    }
    writeHint(null);
    set({ status: 'locked', role: null, profileId: null, label: null, confirmed: true });
  },

  /*
   * A 401 from any authenticated call is the definitive answer the hint was
   * standing in for: this device is not (or no longer) let in. Revoking a lost
   * iPad takes effect on its very next request rather than at the next restart.
   */
  onUnauthorized: () => {
    if (get().status === 'locked') return;
    writeHint(null);
    set({ status: 'locked', role: null, profileId: null, label: null, confirmed: true });
  },
}));

export const selectIsAdmin = (state: AuthState): boolean => state.role === 'admin';
